import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Lock, CreditCard, Trash2, Crown, Loader2, Check, Mail, Calendar, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SettingsPage = ({ user, onAuth }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState({ name: '', email: '', business_name: '', business_type: '', inquiry_email: '', availability: '', calendly_url: '' });
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [limits, setLimits] = useState(null);
  const [subDetails, setSubDetails] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [domainConfig, setDomainConfig] = useState(null);
  const [domainSaving, setDomainSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSiteForDomain, setSelectedSiteForDomain] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    setProfile({
      name: user.name || '',
      email: user.email || '',
      business_name: user.business_name || '',
      business_type: user.business_type || 'photographer',
      inquiry_email: user.inquiry_email || '',
      availability: user.availability || '',
      calendly_url: user.calendly_url || '',
    });
    fetchLimits();
    fetchSubDetails();
    fetchSites();
  }, [user]);

  const fetchLimits = async () => {
    try {
      const res = await axios.get(`${API}/billing/limits`, { withCredentials: true });
      setLimits(res.data);
    } catch {}
  };

  const fetchSubDetails = async () => {
    try {
      const r = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
      setSubDetails(r.data);
    } catch {}
  };

  const fetchSites = async () => {
    try {
      const r = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(r.data.sites || []);
    } catch {}
  };

  const handleConnectDomain = async () => {
    if (!selectedSiteForDomain || !domainInput.trim()) return;
    setDomainSaving(true); setError('');
    try {
      await axios.post(`${API}/sites/${selectedSiteForDomain}/domain`, { domain: domainInput.trim() }, { withCredentials: true });
      setSuccess('Domain connected! Follow the DNS instructions below.');
      // Reload domain config
      const r = await axios.get(`${API}/sites/${selectedSiteForDomain}/domain`, { withCredentials: true });
      setDomainConfig(r.data.domain);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not connect domain.');
    }
    setDomainSaving(false);
  };

  const handleDisconnectDomain = async () => {
    if (!selectedSiteForDomain) return;
    setDomainSaving(true);
    try {
      await axios.delete(`${API}/sites/${selectedSiteForDomain}/domain`, { withCredentials: true });
      setDomainConfig(null); setDomainInput('');
      setSuccess('Domain disconnected.');
    } catch (err) { setError(err.response?.data?.detail || 'Could not disconnect domain.'); }
    setDomainSaving(false);
  };

  const handleProfileSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await axios.patch(`${API}/auth/profile`, profile, { withCredentials: true });
      if (onAuth) onAuth(res.data.user);
      setSuccess('Profile updated');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      setSuccess('Password updated');
      setCurrentPw(''); setNewPw('');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to change password'); }
    setSaving(false);
  };

  const handleManageBilling = async () => {
    try {
      const res = await axios.post(`${API}/billing/portal`, { plan_key: '', origin_url: window.location.origin }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch { setError('No billing account found. Subscribe to a plan first.'); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { setError('Type DELETE to confirm'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/auth/delete-account`, { confirmation: 'DELETE' }, { withCredentials: true });
      if (onAuth) onAuth(null);
      navigate('/');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to delete account'); }
    setSaving(false);
  };

  const planLabel = limits?.plan === 'pro' ? 'Pro' : limits?.plan === 'launch' ? 'Launch' : 'Free';
  const planColor = limits?.plan === 'pro' ? '#C9A84C' : limits?.plan === 'launch' ? '#5ba89d' : '#8c8782';

  const TABS = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'password', label: 'Password', icon: Lock },
    { key: 'domains', label: 'Domains', icon: Globe },
    { key: 'billing', label: 'Billing', icon: CreditCard },
    { key: 'account', label: 'Account', icon: Trash2 },
  ];

  const inputStyle = {
    width: '100%', padding: '12px 14px', fontFamily: 'var(--sans)', fontSize: '14px',
    background: '#fff', border: '1px solid #e8e3dc', color: '#2a2725', borderRadius: '10px',
    outline: 'none', marginBottom: '16px', transition: 'border-color .2s',
  };

  return (
    <div className="builder-v2" style={{ minHeight: '100vh', background: '#faf8f5', color: '#2a2725' }}>
      <div style={{ height: '32px', background: '#0a0907', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
        <a href="https://uncutgems.com" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '0.15em', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>Uncut Gems</a>
        <a href="https://roughcut.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '0.15em', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>RoughCut</a>
        <a href="https://pixally.com" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '0.15em', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>Pixally</a>
        <span style={{ color: '#C9A84C', fontSize: '12px', letterSpacing: '0.15em', fontWeight: 'bold', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>GoldenHour</span>
      </div>
      <nav style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 5vw', borderBottom: '1px solid #e8e3dc', background: '#fff' }}>
        <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782', fontFamily: 'var(--sans)', fontSize: '13px' }}>
          <ArrowLeft size={14} /> Dashboard
        </button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', textDecoration: 'none', letterSpacing: '-.02em' }}>
            golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
          </a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(42,39,37,.35)' }}>by Uncut Gems</span>
        </div>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 5vw' }}>
        <h1 style={{ fontFamily: 'var(--sans)', fontSize: '28px', fontWeight: 600, marginBottom: '32px', color: '#2a2725' }}>Settings</h1>

        <div style={{ display: 'flex', gap: '32px' }}>
          {/* Sidebar tabs */}
          <div style={{ width: '180px', flexShrink: 0 }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setError(''); setSuccess(''); }} data-testid={`settings-tab-${t.key}`} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', marginBottom: '4px',
                background: tab === t.key ? 'rgba(196,162,101,0.06)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                color: tab === t.key ? '#C9A84C' : '#8c8782',
                fontFamily: 'var(--sans)', fontSize: '13px',
                borderLeft: tab === t.key ? '2px solid #C9A84C' : '2px solid transparent',
              }}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1 }}>
            {success && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(91,168,157,0.08)', border: '1px solid rgba(91,168,157,0.2)', marginBottom: '20px', fontSize: '13px', color: '#5ba89d' }}>
                <Check size={14} /> {success}
              </div>
            )}
            {error && (
              <div style={{ padding: '10px 14px', background: 'rgba(196,71,114,0.08)', border: '1px solid rgba(196,71,114,0.2)', marginBottom: '20px', fontSize: '13px', color: '#c47272' }}>
                {error}
              </div>
            )}

            {/* ── Profile ── */}
            {tab === 'profile' && (
              <div>
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '4px' }}>Profile</h2>
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>Your personal information.</p>

                <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Name</label>
                <input type="text" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} style={inputStyle} data-testid="settings-name" />

                <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Email</label>
                <input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} style={inputStyle} data-testid="settings-email" />

                <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Business Name</label>
                <input type="text" value={profile.business_name} onChange={e => setProfile({ ...profile, business_name: e.target.value })} placeholder="Your studio name" style={inputStyle} data-testid="settings-business-name" />

                <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Business Type</label>
                <select value={profile.business_type} onChange={e => setProfile({ ...profile, business_type: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }} data-testid="settings-business-type">
                  <option value="photographer">Photographer</option>
                  <option value="videographer">Videographer</option>
                  <option value="both">Photo & Video</option>
                </select>

                <div style={{ borderTop: '1px solid #e8e3dc', paddingTop: '20px', marginTop: '8px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Mail size={14} style={{ color: '#C9A84C' }} />
                    <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782' }}>Inquiry Email</label>
                  </div>
                  <p style={{ color: '#8c8782', fontSize: '12px', marginBottom: '10px', lineHeight: 1.5 }}>
                    Form submissions from your published site will be sent here. Leave blank to use your account email.
                  </p>
                  <input type="email" value={profile.inquiry_email} onChange={e => setProfile({ ...profile, inquiry_email: e.target.value })} placeholder={profile.email || 'your@email.com'} style={inputStyle} data-testid="settings-inquiry-email" />
                </div>

                <div style={{ borderTop: '1px solid #e8e3dc', paddingTop: '20px', marginTop: '8px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Calendar size={14} style={{ color: '#C9A84C' }} />
                    <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782' }}>Availability Status</label>
                  </div>
                  <p style={{ color: '#8c8782', fontSize: '12px', marginBottom: '10px', lineHeight: 1.5 }}>
                    Shows a badge on your published site so visitors know if you're taking bookings.
                  </p>
                  <select value={profile.availability} onChange={e => setProfile({ ...profile, availability: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }} data-testid="settings-availability">
                    <option value="">No badge (hidden)</option>
                    <option value="booking">Now Booking</option>
                    <option value="limited">Limited Availability</option>
                    <option value="closed">Currently Closed</option>
                  </select>
                </div>

                <div style={{ borderTop: '1px solid #e8e3dc', paddingTop: '20px', marginTop: '8px', marginBottom: '16px' }}>
                  <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '8px' }}>Calendly URL (optional)</label>
                  <p style={{ color: '#8c8782', fontSize: '12px', marginBottom: '10px', lineHeight: 1.5 }}>
                    Paste your Calendly link and any "Book" or "Schedule" buttons on your published site will open it automatically.
                  </p>
                  <input type="url" value={profile.calendly_url} onChange={e => setProfile({ ...profile, calendly_url: e.target.value })} placeholder="https://calendly.com/yourstudio/consult" style={inputStyle} data-testid="settings-calendly" />
                </div>

                <button onClick={handleProfileSave} disabled={saving} className="btn-primary" style={{ marginTop: '8px' }} data-testid="settings-save-profile">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            {/* ── Password ── */}
            {tab === 'password' && (
              <div>
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '4px' }}>Password</h2>
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>Change your password.</p>

                <form onSubmit={handlePasswordChange}>
                  <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Current Password</label>
                  <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required style={inputStyle} autoComplete="current-password" data-testid="settings-current-pw" />

                  <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', letterSpacing: '.02em', color: '#8c8782', display: 'block', marginBottom: '6px' }}>New Password</label>
                  <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={6} placeholder="At least 6 characters" style={inputStyle} autoComplete="new-password" data-testid="settings-new-pw" />

                  <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-change-pw">
                    {saving ? 'Updating...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}


            {/* ── Domains ── */}
            {tab === 'domains' && (
              <div>
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '4px' }}>Custom Domain</h2>
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>
                  Connect your own domain to your GoldenHour site. {limits?.plan !== 'pro' && <span style={{ color: '#C9A84C' }}>Pro plan required.</span>}
                </p>

                {limits?.plan !== 'pro' ? (
                  <div style={{ padding: '24px', background: '#faf8f5', borderRadius: '12px', textAlign: 'center' }}>
                    <Globe size={24} style={{ color: '#8c8782', margin: '0 auto 12px' }} />
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: '#2a2725', marginBottom: '8px', fontWeight: 500 }}>Custom domains are a Pro feature</p>
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '16px' }}>Upgrade to Pro to connect yourstudio.com to your site.</p>
                    <button onClick={() => navigate('/dashboard')} className="btn-primary" style={{ padding: '10px 24px', fontSize: '13px' }}>Upgrade to Pro</button>
                  </div>
                ) : (
                  <div>
                    {/* Select site */}
                    <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Select a site</label>
                    <select
                      value={selectedSiteForDomain || ''}
                      onChange={async (e) => {
                        const sid = e.target.value;
                        setSelectedSiteForDomain(sid);
                        setDomainConfig(null); setDomainInput('');
                        if (sid) {
                          try {
                            const r = await axios.get(`${API}/sites/${sid}/domain`, { withCredentials: true });
                            if (r.data.domain) { setDomainConfig(r.data.domain); setDomainInput(r.data.domain.domain || ''); }
                          } catch {}
                        }
                      }}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">Choose a site...</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.business_name || s.id}</option>)}
                    </select>

                    {selectedSiteForDomain && (
                      <>
                        <label style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', display: 'block', marginBottom: '6px' }}>Domain</label>
                        <input
                          value={domainInput}
                          onChange={e => setDomainInput(e.target.value)}
                          placeholder="yourstudio.com"
                          style={inputStyle}
                          data-testid="domain-input"
                        />

                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          <button onClick={handleConnectDomain} disabled={domainSaving || !domainInput.trim()} className="btn-primary" style={{ padding: '10px 24px', fontSize: '13px' }}>
                            {domainSaving ? 'Connecting...' : (domainConfig ? 'Update Domain' : 'Connect Domain')}
                          </button>
                          {domainConfig && (
                            <button onClick={handleDisconnectDomain} disabled={domainSaving} style={{ padding: '10px 24px', fontSize: '13px', fontFamily: 'var(--sans)', background: '#fff', color: '#c44848', border: '1px solid rgba(196,72,72,0.2)', borderRadius: '10px', cursor: 'pointer' }}>
                              Disconnect
                            </button>
                          )}
                        </div>

                        {/* DNS Instructions */}
                        {domainConfig && domainConfig.dns_records && (
                          <div style={{ padding: '20px', background: '#faf8f5', borderRadius: '10px', border: '1px solid #e8e3dc' }}>
                            <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600, color: '#2a2725', marginBottom: '12px' }}>DNS Setup Instructions</p>
                            <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '12px', lineHeight: 1.6 }}>
                              Add these records at your domain registrar (GoDaddy, Namecheap, Google Domains, etc.):
                            </p>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: '12px', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #e8e3dc' }}>
                              {domainConfig.dns_records.map((r, i) => (
                                <p key={i} style={{ marginBottom: '6px', color: '#2a2725' }}>
                                  <strong>{r.type}</strong> {r.name} → <span style={{ color: '#C9A84C' }}>{r.value}</span>
                                  <span style={{ marginLeft: '8px', fontSize: '10px', color: r.status === 'verified' ? '#5ba89d' : '#C9A84C' }}>
                                    {r.status === 'verified' ? 'Verified' : 'Pending'}
                                  </span>
                                </p>
                              ))}
                            </div>
                            <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', marginTop: '12px' }}>
                              DNS changes can take up to 48 hours to propagate. Your site will be available at both your custom domain and your goldenhour.build subdomain.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* ── Billing ── */}
            {tab === 'billing' && (
              <div>
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '4px' }}>Billing & Subscription</h2>
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>Manage your plan and payment method.</p>

                {/* Plan badge */}
                <div style={{ padding: '20px', border: `1px solid ${planColor === '#8c8782' ? '#e8e3dc' : planColor}`, background: 'rgba(242,239,233,0.02)', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <Crown size={16} style={{ color: planColor }} />
                    <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', letterSpacing: '.02em', color: planColor, fontWeight: 700 }}>{planLabel} Plan</span>
                    {limits?.subscription_status === 'active' && <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#5ba89d', background: 'rgba(91,168,157,0.1)', padding: '2px 6px' }}>ACTIVE</span>}
                  </div>

                  {limits && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', letterSpacing: '.02em', marginBottom: '4px' }}>SITES</p>
                        <p style={{ fontSize: '14px', color: '#2a2725' }}>{limits.sites?.used || 0} / {limits.sites?.limit || 1}</p>
                      </div>
                      <div>
                        <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', letterSpacing: '.02em', marginBottom: '4px' }}>AI EDITS THIS MONTH</p>
                        <p style={{ fontSize: '14px', color: '#2a2725' }}>{limits.edits?.used || 0} / {limits.edits?.limit === 'unlimited' ? 'Unlimited' : limits.edits?.limit || 30}</p>
                      </div>
                      <div>
                        <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', letterSpacing: '.02em', marginBottom: '4px' }}>MULTI-PAGE</p>
                        <p style={{ fontSize: '14px', color: limits.features?.multi_page ? '#5ba89d' : '#8c8782' }}>{limits.features?.multi_page ? 'Enabled' : 'Pro only'}</p>
                      </div>
                      <div>
                        <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', letterSpacing: '.02em', marginBottom: '4px' }}>CUSTOM DOMAIN</p>
                        <p style={{ fontSize: '14px', color: limits.features?.custom_domain ? '#5ba89d' : '#8c8782' }}>{limits.features?.custom_domain ? 'Enabled' : 'Pro only'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {limits?.plan === 'free' ? (
                  <button onClick={() => setShowPricingModal(true)} className="btn-primary" data-testid="settings-upgrade">
                    Upgrade Your Plan
                  </button>
                ) : (
                  <>
                    {/* FIX #9: Show renewal details */}
                    {subDetails && subDetails.current_period_end && (
                      <div style={{ padding: '12px 16px', background: '#faf8f5', borderRadius: '8px', marginBottom: '12px' }}>
                        <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', marginBottom: '4px' }}>
                          Next renewal: <strong style={{ color: '#2a2725' }}>{new Date(subDetails.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                        </p>
                        {subDetails.amount && (
                          <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782' }}>
                            Amount: <strong style={{ color: '#2a2725' }}>${subDetails.amount}/{subDetails.interval}</strong>
                          </p>
                        )}
                      </div>
                    )}
                    <button onClick={handleManageBilling} className="btn-outline" data-testid="settings-manage-billing" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={14} /> Manage Billing
                    </button>
                  </>
                )}

                <p style={{ fontSize: '11px', color: '#8c8782', marginTop: '12px' }}>
                  {limits?.plan !== 'free' ? 'Update payment method, view invoices, or cancel — all managed securely through Stripe.' : 'Subscribe to unlock AI editing, publishing, and more.'}
                </p>
              </div>
            )}

            {/* ── Delete Account ── */}
            {tab === 'account' && (
              <div>
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '4px', color: '#c47272' }}>Delete Account</h2>
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>
                  This will permanently delete your account, all your sites, and cancel your subscription. This cannot be undone.
                </p>

                <div style={{ padding: '20px', border: '1px solid rgba(196,71,114,0.2)', background: 'rgba(196,71,114,0.04)', marginBottom: '20px' }}>
                  <p style={{ fontSize: '13px', color: '#2a2725', marginBottom: '12px' }}>
                    To confirm, type <strong style={{ color: '#c47272' }}>DELETE</strong> below:
                  </p>
                  <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="Type DELETE" style={{ ...inputStyle, borderColor: 'rgba(196,71,114,0.3)' }} data-testid="settings-delete-confirm" />
                  <button onClick={handleDeleteAccount} disabled={deleteConfirm !== 'DELETE' || saving} style={{
                    padding: '10px 20px', fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.02em',
                    background: deleteConfirm === 'DELETE' ? '#c47272' : 'rgba(196,71,114,0.2)',
                    color: deleteConfirm === 'DELETE' ? '#fff' : '#8c8782',
                    border: 'none', cursor: deleteConfirm === 'DELETE' ? 'pointer' : 'default',
                  }} data-testid="settings-delete-btn">
                    {saving ? 'Deleting...' : 'Permanently Delete My Account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
